/**
 * Closed Drive multi-stop trips (local history).
 * A trip is archived when the user has confirmed pickup at every restaurant
 * they ordered from on that plan.
 */

const LS_TRIP_HISTORY = "smartRoute:tripHistory_v1";
export const MAX_DRIVE_TRIP_HISTORY = 40;

function placeLabel(point) {
  return String(point?.formattedAddress || point?.address || "").trim() || null;
}

function normalizeHistoryTrip(row) {
  if (!row || typeof row !== "object") return null;
  const stops = (Array.isArray(row.stops) ? row.stops : [])
    .map((s) => ({
      restaurantId: String(s?.restaurantId || "").trim() || null,
      name: String(s?.name || "").trim() || "Restaurant",
      shopType: s?.shopType || "restaurant",
      pickupWindow: s?.pickupWindow || null,
      pickupDate: s?.pickupDate || null,
      orderId: s?.orderId || null,
      previousOrderIds: Array.isArray(s?.previousOrderIds)
        ? s.previousOrderIds.map(String).filter(Boolean)
        : [],
      tokenNumber: s?.tokenNumber || null,
      tokenNumbers: Array.isArray(s?.tokenNumbers)
        ? s.tokenNumbers.map((t) => String(t || "").trim()).filter(Boolean)
        : s?.tokenNumber
          ? [String(s.tokenNumber).trim()].filter(Boolean)
          : [],
      status: s?.status || "taken",
      takenAt: s?.takenAt || null,
      address: s?.address || null,
      leaveByAt: s?.leaveByAt || null,
      leaveByLabel: s?.leaveByLabel || null,
      driveMinutes: Number.isFinite(Number(s?.driveMinutes)) ? Number(s.driveMinutes) : null,
      routeOffsetMinutes: Number.isFinite(Number(s?.routeOffsetMinutes))
        ? Number(s.routeOffsetMinutes)
        : 0,
      lat: Number.isFinite(Number(s?.lat)) ? Number(s.lat) : null,
      lng: Number.isFinite(Number(s?.lng)) ? Number(s.lng) : null,
    }))
    .filter((s) => s.restaurantId);
  if (!stops.length) return null;
  return {
    id: String(row.id || `trip-${row.closedAt || Date.now()}`),
    fromLabel: placeLabel(row.origin) || row.fromLabel || null,
    toLabel: placeLabel(row.destination) || row.toLabel || null,
    origin: row.origin || null,
    destination: row.destination || null,
    leaveByLabel: row.leaveByLabel || null,
    tripStartedAt: row.tripStartedAt || null,
    routeDurationMinutes: Number.isFinite(Number(row.routeDurationMinutes))
      ? Number(row.routeDurationMinutes)
      : null,
    departureIso: row.departureIso || null,
    closedAt: row.closedAt || new Date().toISOString(),
    stops,
  };
}

/**
 * Rebuild a multi-stop plan shape so DriveMultiStopPlanBar can render history.
 */
export function historyTripToPlan(trip) {
  if (!trip?.stops?.length) return null;
  const origin =
    trip.origin ||
    (trip.fromLabel ? { formattedAddress: trip.fromLabel, address: trip.fromLabel } : null);
  const destination =
    trip.destination ||
    (trip.toLabel ? { formattedAddress: trip.toLabel, address: trip.toLabel } : null);
  return {
    origin,
    destination,
    leaveByLabel: trip.leaveByLabel || null,
    tripStartedAt: trip.tripStartedAt || null,
    routeDurationMinutes: trip.routeDurationMinutes ?? null,
    departureIso: trip.departureIso || null,
    maxStops: Math.max(trip.stops.length, 1),
    stops: trip.stops.map((s, i) => ({
      restaurantId: s.restaurantId,
      name: s.name,
      shopType: s.shopType || "restaurant",
      status: s.status || "taken",
      pickupWindow: s.pickupWindow || null,
      pickupDate: s.pickupDate || null,
      orderId: s.orderId || null,
      previousOrderIds: s.previousOrderIds || [],
      tokenNumber: s.tokenNumber || null,
      tokenNumbers: s.tokenNumbers || [],
      takenAt: s.takenAt || null,
      address: s.address || null,
      leaveByAt: s.leaveByAt || null,
      leaveByLabel: s.leaveByLabel || trip.leaveByLabel || null,
      driveMinutes: s.driveMinutes,
      routeOffsetMinutes: Number(s.routeOffsetMinutes) || i * 5,
      lat: s.lat,
      lng: s.lng,
    })),
    savedAt: Date.now(),
    fromHistory: true,
    historyId: trip.id || null,
  };
}

export function loadDriveTripHistory() {
  try {
    const raw = localStorage.getItem(LS_TRIP_HISTORY);
    const list = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(list)) return [];
    return list.map(normalizeHistoryTrip).filter(Boolean);
  } catch {
    return [];
  }
}

function persistDriveTripHistory(list) {
  try {
    localStorage.setItem(
      LS_TRIP_HISTORY,
      JSON.stringify((Array.isArray(list) ? list : []).slice(0, MAX_DRIVE_TRIP_HISTORY)),
    );
  } catch {
    /* ignore */
  }
}

/**
 * Archive a completed multi-stop plan into trip history (newest first).
 * @returns {object|null} archived trip
 */
export function archiveDriveTripToHistory(plan, { closedAt } = {}) {
  const paidStops = (Array.isArray(plan?.stops) ? plan.stops : []).filter(
    (s) => s?.status === "ordered" || s?.status === "ready" || s?.status === "taken",
  );
  if (!paidStops.length) return null;

  const closedIso = closedAt || new Date().toISOString();
  const trip = normalizeHistoryTrip({
    id: `trip-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    origin: plan.origin || null,
    destination: plan.destination || null,
    leaveByLabel: plan.leaveByLabel || null,
    tripStartedAt: plan.tripStartedAt || null,
    routeDurationMinutes: plan.routeDurationMinutes ?? null,
    departureIso: plan.departureIso || null,
    closedAt: closedIso,
    stops: paidStops
      .slice()
      .sort(
        (a, b) =>
          (Number(a.routeOffsetMinutes) || 0) - (Number(b.routeOffsetMinutes) || 0),
      )
      .map((s) => {
        const tokenNumbers = Array.isArray(s.tokenNumbers)
          ? s.tokenNumbers.map((t) => String(t || "").trim()).filter(Boolean)
          : [];
        const single = String(s.tokenNumber || "").trim();
        if (single && !tokenNumbers.includes(single)) tokenNumbers.push(single);
        return {
          restaurantId: s.restaurantId,
          name: s.name,
          shopType: s.shopType,
          pickupWindow: s.pickupWindow,
          pickupDate: s.pickupDate,
          orderId: s.orderId,
          previousOrderIds: s.previousOrderIds,
          tokenNumber: tokenNumbers[tokenNumbers.length - 1] || single || null,
          tokenNumbers,
          status: "taken",
          takenAt: s.takenAt || closedIso,
          address: s.address,
          leaveByAt: s.leaveByAt || null,
          leaveByLabel: s.leaveByLabel || plan.leaveByLabel || null,
          driveMinutes: s.driveMinutes ?? null,
          routeOffsetMinutes: s.routeOffsetMinutes ?? 0,
          lat: s.lat ?? null,
          lng: s.lng ?? null,
        };
      }),
  });
  if (!trip) return null;

  const existing = loadDriveTripHistory();
  const fp = tripOrderFingerprint(trip);
  if (fp && existing.some((t) => tripOrderFingerprint(t) === fp)) {
    return existing.find((t) => tripOrderFingerprint(t) === fp) || trip;
  }
  persistDriveTripHistory([trip, ...existing].slice(0, MAX_DRIVE_TRIP_HISTORY));
  return trip;
}

function tripOrderFingerprint(trip) {
  const ids = (Array.isArray(trip?.stops) ? trip.stops : [])
    .flatMap((s) => [
      ...(Array.isArray(s?.previousOrderIds) ? s.previousOrderIds : []),
      s?.orderId,
    ])
    .map((id) => String(id || "").trim())
    .filter(Boolean)
    .sort();
  return ids.join(",");
}

/**
 * Merge server-closed trips (and optional local snapshots) into Recent trips history.
 * Dedupes by order-id fingerprint so owner-confirm / multi-device sync does not duplicate.
 * @returns {number} how many new trips were added
 */
export function mergeRecentTripsIntoHistory(trips) {
  const incoming = (Array.isArray(trips) ? trips : [])
    .map((row) => normalizeHistoryTrip(row))
    .filter(Boolean);
  if (!incoming.length) return 0;

  const existing = loadDriveTripHistory();
  const fingerprints = new Set(
    existing.map((t) => tripOrderFingerprint(t)).filter(Boolean),
  );
  const toAdd = [];
  for (const trip of incoming) {
    const fp = tripOrderFingerprint(trip);
    if (fp && fingerprints.has(fp)) continue;
    if (fp) fingerprints.add(fp);
    toAdd.push(trip);
  }
  if (!toAdd.length) return 0;
  persistDriveTripHistory([...toAdd, ...existing].slice(0, MAX_DRIVE_TRIP_HISTORY));
  return toAdd.length;
}

export function removeDriveTripFromHistory(tripId) {
  const id = String(tripId || "");
  if (!id) return loadDriveTripHistory();
  const trips = loadDriveTripHistory().filter((t) => String(t.id) !== id);
  persistDriveTripHistory(trips);
  return trips;
}

export function formatDriveTripHistoryTitle(trip, fallback = "Trip") {
  const from = String(trip?.fromLabel || "").split(",")[0]?.trim();
  const to = String(trip?.toLabel || "").split(",")[0]?.trim();
  if (from && to) return `${from} → ${to}`;
  if (from) return from;
  const names = (trip?.stops || []).map((s) => s.name).filter(Boolean);
  if (names.length) return names.join(" · ");
  return fallback;
}
